import React, { useState, useEffect, useRef } from 'react';
import { showToast } from '../../utils/helpers';
import { generateCreditTopUpLink, verifyCreditTopUp } from '../../services/creditTopUpService';

const QUICK_AMOUNTS = [1000, 2500, 5000, 10000, 25000, 50000];

const MIN_TOPUP = 100;
const MAX_TOPUP = 1000000;

const formatQuickAmount = (n) =>
  n >= 1000 ? `₦${(n / 1000).toLocaleString()}k` : `₦${n.toLocaleString()}`;

const BillingSettings = ({ user }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [creditBalance, setCreditBalance] = useState(
    Number(user?.credit_balance) || Number(user?.creditBalance) || 0
  );

  // Top-up modal state
  const [isTopUpOpen, setIsTopUpOpen] = useState(false);
  const [topUpStep, setTopUpStep] = useState('input'); // 'input' | 'paystack' | 'success'
  const [amount, setAmount] = useState('');
  const [paystackLoading, setPaystackLoading] = useState(false);
  const [paystackUrl, setPaystackUrl] = useState(null);
  const [paystackRef, setPaystackRef] = useState(null);
  const [paystackIframeLoaded, setPaystackIframeLoaded] = useState(false);
  const [paystackPolling, setPaystackPolling] = useState(false);
  const [paymentFailed, setPaymentFailed] = useState(false);
  const paystackPollRef = useRef(null);

  const userBankDetails = {
    bankName: user?.bank_name,
    accountNumber: user?.account_number,
    accountName: user?.account_name,
  };
  const bankDetails = userBankDetails || {
    bankName: "Wema Bank",
    accountNumber: "0123456789",
    accountName: "N/A"
  };

  const dateCreated = user?.dateCreated || "January 15, 2023";

  // Long polling for Paystack payment verification (mirrors TipModal)
  useEffect(() => {
    if (topUpStep !== 'paystack' || !paystackRef) {
      if (paystackPollRef.current) {
        clearInterval(paystackPollRef.current);
        paystackPollRef.current = null;
      }
      return;
    }

    setPaystackPolling(true);
    setPaymentFailed(false);
    let attempts = 0;
    const maxAttempts = 60; // 60 × 3s = 3 minutes max

    paystackPollRef.current = setInterval(async () => {
      attempts++;
      if (attempts > maxAttempts) {
        if (paystackPollRef.current) clearInterval(paystackPollRef.current);
        setPaystackPolling(false);
        setPaymentFailed(true);
        return;
      }

      try {
        const result = await verifyCreditTopUp(paystackRef);
        if (result.success) {
          if (paystackPollRef.current) clearInterval(paystackPollRef.current);
          setPaystackPolling(false);
          if (typeof result.data?.credit_balance === 'number') {
            setCreditBalance(result.data.credit_balance);
          } else {
            // Fall back to the display value in the response payload
            const credited = result.data?.amount_credited || Number(amount) || 0;
            setCreditBalance((prev) => (Number(prev) || 0) + credited);
          }
          setPaystackUrl(null);
          setPaystackRef(null);
          setTopUpStep('success');
        }
      } catch (err) {
        // Keep polling on transient errors
      }
    }, 3000);

    return () => {
      if (paystackPollRef.current) {
        clearInterval(paystackPollRef.current);
        paystackPollRef.current = null;
      }
    };
  }, [topUpStep, paystackRef, amount]);

  // Close modal / handle Escape key
  useEffect(() => {
    if (!isTopUpOpen) return;
    const handleEsc = (e) => {
      if (e.key === 'Escape') closeTopUpModal();
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isTopUpOpen, topUpStep, paystackRef]);

  const closeTopUpModal = () => {
    if (paystackPollRef.current) {
      clearInterval(paystackPollRef.current);
      paystackPollRef.current = null;
    }
    setIsTopUpOpen(false);
    setTopUpStep('input');
    setAmount('');
    setPaystackUrl(null);
    setPaystackRef(null);
    setPaystackIframeLoaded(false);
    setPaystackPolling(false);
    setPaymentFailed(false);
  };

  const handleOpenTopUp = () => {
    setIsTopUpOpen(true);
    setTopUpStep('input');
    setAmount('');
    setPaystackUrl(null);
    setPaystackRef(null);
    setPaystackIframeLoaded(false);
    setPaystackPolling(false);
    setPaymentFailed(false);
  };

  const numAmount = Number(amount);
  const amountValid = amount !== '' && !isNaN(numAmount) && numAmount >= MIN_TOPUP && numAmount <= MAX_TOPUP;

  const handleGeneratePaymentLink = async () => {
    if (!amountValid) return;
    setPaystackLoading(true);
    try {
      const result = await generateCreditTopUpLink({ amount: numAmount });
      const paymentUrl = result.data?.payment_url
        || result.data?.payment_link
        || result.data?.authorization_url;
      const paymentRef = result.data?.reference;

      if (result.success && paymentUrl && paymentRef) {
        setPaystackUrl(paymentUrl);
        setPaystackRef(paymentRef);
        setPaystackIframeLoaded(false);
        setPaymentFailed(false);
        setTopUpStep('paystack');
      } else {
        showToast(result.error || 'Failed to generate payment link', 'error');
      }
    } catch (err) {
      showToast(err?.message || 'Failed to open payment. Please try again.', 'error');
    } finally {
      setPaystackLoading(false);
    }
  };

  const handleEditBankDetails = () => {
    showToast('Bank details editing feature coming soon', 'info');
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      showToast('Billing settings saved successfully', 'success');
    } catch (error) {
      showToast('Failed to save billing settings', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    showToast('Changes cancelled', 'info');
  };

  const hasBankDetails = bankDetails?.bankName || bankDetails?.accountNumber || bankDetails?.accountName;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Credit Balance Card */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <h3 className="text-lg font-semibold mb-4">Credit Balance</h3>

          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm text-gray-500 mb-1">Available Credits</p>
                <p className="text-2xl font-bold text-blue-700">
                  ₦{Number(creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>
          </div>

          <p className="text-gray-600 text-sm mb-6">
            Top up your credit balance to pay for invoices and other Paymeo services.{' '}
            <a href="#bills" className="text-blue-600 hover:text-blue-800 transition-colors underline">
              Learn More
            </a>
          </p>

          <button
            onClick={handleOpenTopUp}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Top Up
          </button>
        </div>

        {/* Account Info Card */}
        {hasBankDetails ? (
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold ">Account Info</h3>
              <span className="text-sm text-gray-500">Created on {dateCreated}</span>
            </div>

            <div className="mb-6 p-4 bg-gray-100 rounded-lg">
              <div className="flex items-center gap-4 mb-4">
                <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bank Account</p>
                  <p className="font-semibold ">{bankDetails.bankName}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Account Number</span>
                  <span className="font-medium ">{bankDetails.accountNumber}</span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Account Name</span>
                  <span className="font-medium ">{bankDetails.accountName}</span>
                </div>
              </div>
            </div>

            <p className="text-gray-600 text-sm mb-4 italic">
              This your default account for withdrawals
            </p>

            <button
              onClick={handleEditBankDetails}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edit Bank Details
            </button>
          </div>
        ) : (
          <></>
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
        <button
          onClick={handleSave}
          disabled={isLoading}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : 'Save Changes'}
        </button>
        <button
          onClick={handleCancel}
          className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
      </div>

      {/* ===== Top-Up Modal ===== */}
      {isTopUpOpen && (
        <div className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4">
          <div className="absolute inset-0" onClick={closeTopUpModal} />

          <div className="relative w-full sm:w-[420px] bg-white rounded-t-[24px] sm:rounded-[28px] shadow-2xl overflow-hidden sm:max-h-[92vh] flex flex-col">
            {/* Mobile grabber */}
            <div className="pt-2.5 pb-0.5 sm:hidden">
              <div className="mx-auto w-10 h-1 rounded-full bg-gray-200" />
            </div>

            {/* Header */}
            <div className="px-4 py-3.5 flex items-center justify-between shrink-0 border-b border-gray-100">
              <button
                onClick={() => {
                  if (topUpStep === 'input' || topUpStep === 'success') {
                    closeTopUpModal();
                  } else {
                    setPaystackUrl(null);
                    setPaystackRef(null);
                    setPaymentFailed(false);
                    setTopUpStep('input');
                  }
                }}
                className="p-2 -ml-1 hover:bg-gray-100 rounded-full transition-colors"
                aria-label="Close"
              >
                {topUpStep === 'input' ? (
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                )}
              </button>
              <h2 className="text-[15px] font-semibold text-gray-900">
                {topUpStep === 'paystack' ? 'Payment' : topUpStep === 'success' ? 'Success' : 'Top Up Credit'}
              </h2>
              <div className="w-9" />
            </div>

            <div className="overflow-y-auto flex-1">
              {/* Step: input */}
              {topUpStep === 'input' && (
                <div className="px-5 pb-6 pt-6 space-y-6">
                  <div className="flex flex-col items-center text-center pt-2">
                    <div className="text-base font-semibold text-gray-900">
                      Enter amount to top up
                    </div>
                    <div className="text-sm text-gray-500 mt-0.5">
                      Current balance: ₦{Number(creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-center gap-1 bg-gray-50 rounded-2xl px-4 py-4 group focus-within:ring-2 ring-blue-300 transition-shadow">
                      <span className="text-4xl font-bold text-gray-400 group-focus-within:text-blue-600 transition-colors select-none">₦</span>
                      <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="0"
                        min={MIN_TOPUP}
                        max={MAX_TOPUP}
                        className="w-44 bg-transparent text-center text-4xl font-bold outline-none text-gray-900 placeholder:text-gray-300 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                    </div>
                    <div className="flex flex-wrap justify-center gap-2">
                      {QUICK_AMOUNTS.map(n => {
                        const selected = numAmount === n;
                        return (
                          <button
                            key={n}
                            onClick={() => setAmount(String(n))}
                            className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${
                              selected
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-gray-50 text-gray-600 border-transparent hover:bg-gray-100'
                            }`}
                          >
                            {formatQuickAmount(n)}
                          </button>
                        );
                      })}
                    </div>
                    {amount !== '' && !amountValid && (
                      <div className="text-xs text-red-500 text-center">
                        Amount must be between ₦{MIN_TOPUP.toLocaleString()} and ₦{MAX_TOPUP.toLocaleString()}
                      </div>
                    )}
                  </div>

                  {/* Payment method */}
                  <div>
                    <div className="flex items-center justify-between bg-gray-50 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                          <svg className="w-[18px] h-[18px] text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                          </svg>
                        </div>
                        <div className="text-left">
                          <div className="text-sm font-semibold text-gray-900 leading-tight">Card, USSD, Transfer</div>
                          <div className="text-xs text-gray-500">via Paystack</div>
                        </div>
                      </div>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>

                  <button
                    onClick={handleGeneratePaymentLink}
                    disabled={!amountValid || paystackLoading}
                    className="w-full py-3.5 rounded-2xl text-[15px] font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {paystackLoading ? 'Initializing...' : amountValid ? `Top Up ₦${numAmount.toLocaleString()}` : 'Top Up'}
                  </button>
                </div>
              )}

              {/* Step: paystack payment */}
              {topUpStep === 'paystack' && (
                <div className="flex flex-col" style={{ minHeight: '60vh' }}>
                  {!paystackIframeLoaded && !paymentFailed && (
                    <div className="flex-1 flex items-center justify-center py-16">
                      <div className="text-center">
                        <div className="w-10 h-10 border-3 border-blue-600 border-t-transparent animate-spin rounded-full mx-auto" />
                        <p className="mt-3 text-gray-500 text-sm font-medium">Loading payment gateway...</p>
                      </div>
                    </div>
                  )}

                  {paymentFailed && (
                    <div className="flex-1 flex items-center justify-center py-16 px-6">
                      <div className="text-center space-y-4">
                        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                          <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.734 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">Payment not completed</h3>
                          <p className="text-sm text-gray-500 mt-1">The payment could not be verified. Please try again.</p>
                        </div>
                        <div className="flex gap-3">
                          <button
                            className="flex-1 py-3 rounded-2xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700"
                            onClick={() => {
                              setPaystackUrl(null);
                              setPaystackRef(null);
                              setPaymentFailed(false);
                              setTopUpStep('input');
                            }}
                          >
                            Try Again
                          </button>
                          <button
                            className="flex-1 py-3 rounded-2xl text-sm font-semibold border border-gray-300 text-gray-700 hover:bg-gray-100"
                            onClick={closeTopUpModal}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {paystackUrl && !paymentFailed && (
                    <div className={`flex-1 ${!paystackIframeLoaded ? 'hidden' : ''}`} style={{ minHeight: '45vh' }}>
                      <iframe
                        src={paystackUrl}
                        title="Paystack Payment"
                        className="w-full h-full border-0 rounded-xl"
                        style={{ minHeight: '45vh' }}
                        onLoad={() => setPaystackIframeLoaded(true)}
                        sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-popups-to-escape-sandbox"
                        allow="payment *"
                      />
                    </div>
                  )}

                  <div className="px-4 py-3 shrink-0">
                    <div className="flex items-center justify-center gap-2">
                      {paystackPolling && (
                        <div className="w-3 h-3 border-2 border-green-500 border-t-transparent animate-spin rounded-full" />
                      )}
                      <p className="text-xs text-gray-500 text-center font-medium">
                        {paystackPolling
                          ? 'Waiting for payment confirmation...'
                          : 'Complete your payment in the secure form above.'}
                      </p>
                    </div>
                    <button
                      onClick={() => {
                        if (paystackUrl) window.open(paystackUrl, '_blank');
                      }}
                      className="w-full mt-2 text-xs text-blue-600 font-semibold hover:underline text-center"
                    >
                      Open in new tab instead
                    </button>
                  </div>
                </div>
              )}

              {/* Step: success */}
              {topUpStep === 'success' && (
                <div className="px-8 pb-8 pt-6 text-center space-y-6 flex flex-col items-center justify-center min-h-[380px]">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-green-50 ring-8 ring-green-500/10 flex items-center justify-center">
                      <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <h3 className="text-xl font-bold text-gray-900 leading-tight">Top-up successful</h3>
                    <p className="text-sm text-gray-500 font-medium max-w-[260px] mx-auto leading-relaxed">
                      Your credit balance has been updated.
                      <br />
                      <span className="font-semibold text-gray-700">₦{Number(creditBalance || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    </p>
                  </div>
                  <div className="w-full pt-2">
                    <button className="w-full py-3.5 rounded-2xl text-[15px] font-semibold bg-blue-600 text-white hover:bg-blue-700" onClick={closeTopUpModal}>
                      Done
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingSettings;
